import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng';
import { Params, ActivatedRoute, Router } from '@angular/router';
import { ITaskInfo, ITaskLocationUpdateContext, IVehicleAddress } from '../../interfaces/task-info';
import { BBWTMessageService, SettingsService, UserService } from '../../services';
import { TaskService } from '../../services';
import { MarkerPoint } from '../map-control/marker.point';
import { get } from 'lodash';
import { VehicleAddressService } from '../../services';
import {
  CustomerPriceResult,
  SupplierPriceResult,
} from 'src/app/interfaces/customer-price-result/customer-price-result';
import { OrganisationTypeEnum } from 'src/app/enums/organisation-types';
import { Message } from 'src/app/classes/Message';
import { AddressAutoCompletionService } from '../../services/address-auto-completion.service';
import { removeSlashesOnPaste } from '../../utils/input.helper';
import { switchMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { isValidUKPostcodeFormat } from '../../utils/postcode-format-validation';

@Component({
  selector: 'edit-task-location',
  styleUrls: ['./edit-task-location.component.scss'],
  templateUrl: './edit-task-location.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class EditTaskLocationComponent implements OnInit {
  public customerPriceResult: CustomerPriceResult;

  public supplierPriceResult: SupplierPriceResult;

  public isPriceLoading = false;

  public isSearchPickup: boolean = false;

  public isSaving: boolean = false;

  public updateDropoffWindow: boolean = true;

  public context: ITaskLocationUpdateContext;

  public hasCustomerBookingRole: boolean = false;
  public hasCustomerPriceRole: boolean = false;

  public get customerPriceHeader() {
    return this.context?.isVme ? 'Customer Price Change' : 'Price Change';
  }

  public get taskGrabbed() {
    return this.context?.supplierAssigned;
  }

  public get isPickupLocation(): boolean {
    return this.pickupLocation != null;
  }

  public set pickupLocation(value: IVehicleAddress) {
    if (this.objectComparer(value, this.task.pickupLocation)) {
      return;
    }

    this.addressSearchPickupLocation = { ...value };
    this.displayAddressSearchPickupLocationManualSave = false;

    this.isPriceLoading = true;

    this.task.pickupLocation = value;

    this.calculateUpdatedTaskPrice();
  }

  public get pickupLocation(): IVehicleAddress {
    return this.task?.pickupLocation;
  }

  public isSearchDropoff: boolean = false;

  public get isDropoffLocation(): boolean {
    return this.dropoffLocation != null;
  }

  public set dropoffLocation(value: IVehicleAddress) {
    if (this.objectComparer(value, this.task.dropoffLocation)) {
      return;
    }

    this.addressSearchDropoffLocation = { ...value };
    this.displayAddressSearchDropoffLocationManualSave = false;

    this.isPriceLoading = true;

    this.task.dropoffLocation = value;

    this.calculateUpdatedTaskPrice();
  }

  public get dropoffLocation(): IVehicleAddress {
    return this.task?.dropoffLocation;
  }

  public get originMarkerPoint(): MarkerPoint {
    return !this.pickupLocation
      ? null
      : new MarkerPoint(this.pickupLocation.latitude, this.pickupLocation.longitude);
  }

  public set originMarkerPoint(value: MarkerPoint) {
    if (
      value &&
      value.longitude != get(this.pickupLocation, 'longitude') &&
      value.latitude != get(this.pickupLocation, 'latitude')
    ) {
      this.isSearchPickup = true;
      this.vehicleAddressService.GetFromCoord(value.latitude, value.longitude).then((address) => {
        this.pickupLocation = address;
        this.isSearchPickup = false;
      });
    }
  }

  public get destinationMarkerPoint(): MarkerPoint {
    return !this.dropoffLocation
      ? null
      : new MarkerPoint(this.dropoffLocation.latitude, this.dropoffLocation.longitude);
  }

  public set destinationMarkerPoint(value: MarkerPoint) {
    if (
      value &&
      value.longitude != get(this.dropoffLocation, 'longitude') &&
      value.latitude != get(this.dropoffLocation, 'latitude')
    ) {
      this.isSearchDropoff = true;
      this.vehicleAddressService.GetFromCoord(value.latitude, value.longitude).then((address) => {
        this.dropoffLocation = address;
        this.isSearchDropoff = false;
      });
    }
  }

  private task: ITaskInfo;

  public addressSearchPickupLocation: IVehicleAddress = null;
  public addressSearchDropoffLocation: IVehicleAddress = null;
  public displayAddressSearchPickupLocationManualSave: boolean = false;
  public displayAddressSearchDropoffLocationManualSave: boolean = false;
  protected isVmeUser: boolean = false;
  private customerId = 0;
  isCustomerRecalculationDisabled = false;
  isPickupPostcodeValidUKPostcodeFormat = true;
  isDropoffPostcodeValidUKPostcodeFormat = true;

  constructor(
    private taskService: TaskService,
    private router: Router,
    public messageService: BBWTMessageService,
    private primeNgMessageService: MessageService,
    private route: ActivatedRoute,
    private vehicleAddressService: VehicleAddressService,
    private addressAutoCompletionService: AddressAutoCompletionService,
    private systemSettingsService: SettingsService,
    private userService: UserService,
  ) {}

  ngOnInit() {
    this.route.params
      .pipe(
        switchMap((params) => {
          const taskId = params['id'];
          return forkJoin([
            this.taskService.getTaskLocationUpdateContext(taskId),
            this.taskService.GetTask(taskId),
            this.userService.getCurrentUserOrg(),
          ]);
        }),
        switchMap(([taskLocationUpdateResult, taskInfoResult, organisation]) => {
          this.task = taskInfoResult;
          this.customerId = taskInfoResult?.customerInfo?.id;
          this.addressSearchPickupLocation = { ...taskInfoResult.pickupLocation };
          this.addressSearchDropoffLocation = { ...taskInfoResult.dropoffLocation };

          this.context = taskLocationUpdateResult;
          this.isVmeUser = organisation.organisationTypeId == 1;

          return this.systemSettingsService.GetSystemSettings();
        }),
      )
      .subscribe((systemSettings) => {
        const disabledRecalculationCustomerIds =
          systemSettings.disabledAutomaticPriceRecalculationCustomerIds.split(',');

        this.isCustomerRecalculationDisabled = disabledRecalculationCustomerIds.includes(
          this.task.customerInfo.id.toString(),
        );
        this.initLocationInputs();
      });

    this.initRoles();
  }

  private async initRoles() {
    this.hasCustomerBookingRole = await this.userService.isUserInRole('CUSTOMER_BOOKING');
    this.hasCustomerPriceRole = await this.userService.isUserInRole('CUSTOMER_PRICE');
  }

  initLocationInputs() {
    const fieldsPickUp = [
      {
        element: 'searchPickUp',
        field: '',
        parentContainer: 'searchPickUpContainer',
        showSavedSites: true,
        customerId: this.isVmeUser ? this.customerId : undefined,
      },
    ];

    this.addressAutoCompletionService.useWithVehicleAddressConverter(
      fieldsPickUp,
      (address) => {
        this.pickupLocation = address;
      },
      (savedSite) => {
        this.pickupLocation = {
          addressLine1: savedSite.address1,
          addressLine2: savedSite.address2,
          addressLine3: savedSite.address3,
          businessName: savedSite.businessName,
          primaryAddress: savedSite.primaryAddress,
          city: savedSite.city,
          id: 0,
          postcode: savedSite.postcode,
          latitude: savedSite.latitude,
          longitude: savedSite.longitude,
          what3Words: savedSite.what3Words,
        } as IVehicleAddress;
      },
      () => {
        this.addressSearchPickupLocation = {
          addressLine1: null,
          addressLine2: null,
          addressLine3: null,
          businessName: null,
          city: null,
          country: null,
          id: 0,
          postal_town: null,
          postcode: null,
          primaryAddress: null,
          route: null,
          secondary: null,
          street_number: null,
          latitude: null,
          longitude: null,
          manualEntry: true,
        };
      },
    );

    const fieldsDropoff = [
      {
        element: 'searchDropoff',
        field: '',
        parentContainer: 'searchDropoffContainer',
        showSavedSites: true,
        customerId: this.isVmeUser ? this.customerId : undefined,
      },
    ];

    this.addressAutoCompletionService.useWithVehicleAddressConverter(
      fieldsDropoff,
      (address) => {
        this.dropoffLocation = address;
      },
      (savedSite) => {
        this.dropoffLocation = {
          addressLine1: savedSite.address1,
          addressLine2: savedSite.address2,
          addressLine3: savedSite.address3,
          businessName: savedSite.businessName,
          city: savedSite.city,
          primaryAddress: savedSite.primaryAddress,
          id: 0,
          postcode: savedSite.postcode,
          latitude: savedSite.latitude,
          longitude: savedSite.longitude,
          what3Words: savedSite.what3Words,
        } as IVehicleAddress;
      },
      () => {
        this.addressSearchDropoffLocation = {
          addressLine1: null,
          addressLine2: null,
          addressLine3: null,
          businessName: null,
          city: null,
          country: null,
          id: 0,
          postal_town: null,
          postcode: null,
          primaryAddress: null,
          route: null,
          secondary: null,
          street_number: null,
          latitude: null,
          longitude: null,
          manualEntry: true,
        };
      },
    );
  }

  objectComparer(x, y) {
    if (x === y) return true;
    // if both x and y are null or undefined and exactly the same

    if (!(x instanceof Object) || !(y instanceof Object)) return false;
    // if they are not strictly equal, they both need to be Objects

    if (x.constructor !== y.constructor) return false;
    // they must have the exact same prototype chain, the closest we can do is
    // test there constructor.

    for (var p in x) {
      if (!x.hasOwnProperty(p)) continue;
      // other properties were tested using x.constructor === y.constructor

      if (!y.hasOwnProperty(p)) return false;
      // allows to compare x[ p ] and y[ p ] when set to undefined

      if (x[p] === y[p]) continue;
      // if they have the same strict value or identity then they are equal

      if (typeof x[p] !== 'object') return false;
      // Numbers, Strings, Functions, Booleans must be strictly equal

      if (!this.objectComparer(x[p], y[p])) return false;
      // Objects and Arrays must be tested recursively
    }

    for (p in y) {
      if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
      // allows x[ p ] to be set to undefined
    }
    return true;
  }

  cancel() {
    this.backToJob();
  }

  async saveClicked() {
    try {
      this.isSaving = true;
      await this.taskService.saveNewLocations(this.task, this.updateDropoffWindow, false);
      this.primeNgMessageService.add(Message.Success('Task Location Updated', `Success`));
      this.backToJob();
    } catch (e) {
      this.primeNgMessageService.add(Message.Error('Task Location Update Failed', `Error`));
    } finally {
      this.isSaving = false;
    }
  }

  backToJob() {
    if (this.context?.isVme) {
      this.router.navigate(['/app/jobscreen/' + this.task.jobId]);
    } else {
      this.router.navigate(['/app/jobscreen/' + this.task.jobId + '/' + this.task.orderId]);
    }
  }

  onPickupAddressManualEntry(): void {
    this.isPickupPostcodeValidUKPostcodeFormat = isValidUKPostcodeFormat(
      this.addressSearchPickupLocation.postcode,
    );
    this.addressSearchPickupLocation.manualEntry = true;
    this.displayAddressSearchPickupLocationManualSave = true;
  }

  savePickupAddressManualEntry(): void {
    if (
      this.isAddressParamNullOrEmpty(this.addressSearchPickupLocation.addressLine1) ||
      this.isAddressParamNullOrEmpty(this.addressSearchPickupLocation.city) ||
      this.isAddressParamNullOrEmpty(this.addressSearchPickupLocation.postcode)
    ) {
      this.messageService.ShowWarning(
        '',
        'Please make sure you have filled out all the required fields',
      );
      return;
    }

    let address = this.addressAutoCompletionService.formatVehicleAddress(
      this.addressSearchPickupLocation,
    );

    this.addressAutoCompletionService.validateAddress(address).subscribe((validationResponse) => {
      if (validationResponse == null || validationResponse.status != 200) {
        this.messageService.ShowWarning('', "The address details you've entered are not valid");
        return;
      } else {
        this.messageService.ShowSuccess('', 'Collection Address Updated');
        this.addressSearchPickupLocation.primaryAddress = address;
        this.addressSearchPickupLocation.latitude = validationResponse.address.latitude;
        this.addressSearchPickupLocation.longitude = validationResponse.address.longitude;
        this.pickupLocation = { ...this.addressSearchPickupLocation };
      }
    });
  }

  onDropoffAddressManualEntry(): void {
    this.isDropoffPostcodeValidUKPostcodeFormat = isValidUKPostcodeFormat(
      this.addressSearchDropoffLocation.postcode,
    );
    this.addressSearchDropoffLocation.manualEntry = true;
    this.displayAddressSearchDropoffLocationManualSave = true;
  }

  saveDropoffAddressManualEntry(): void {
    if (
      this.isAddressParamNullOrEmpty(this.addressSearchDropoffLocation.addressLine1) ||
      this.isAddressParamNullOrEmpty(this.addressSearchDropoffLocation.city) ||
      this.isAddressParamNullOrEmpty(this.addressSearchDropoffLocation.postcode)
    ) {
      this.messageService.ShowWarning(
        '',
        'Please make sure you have filled out all the required fields',
      );
      return;
    }

    let address = this.addressAutoCompletionService.formatVehicleAddress(
      this.addressSearchDropoffLocation,
    );

    this.addressAutoCompletionService.validateAddress(address).subscribe((validationResponse) => {
      if (validationResponse == null || validationResponse.status != 200) {
        this.messageService.ShowWarning('', "The address details you've entered are not valid");
        return;
      } else {
        this.messageService.ShowSuccess('', 'Delivery Address Updated');
        this.addressSearchDropoffLocation.primaryAddress = address;
        this.addressSearchDropoffLocation.latitude = validationResponse.address.latitude;
        this.addressSearchDropoffLocation.longitude = validationResponse.address.longitude;
        this.dropoffLocation = { ...this.addressSearchDropoffLocation };
      }
    });
  }

  isAddressParamNullOrEmpty(value: string | null | undefined): boolean {
    return value == null || value.trim().length === 0;
  }

  calculateUpdatedTaskPrice(): void {
    this.taskService
      .saveNewLocations(this.task, this.updateDropoffWindow, true)
      .then((result) => {
        this.customerPriceResult = result.customer;
        this.supplierPriceResult = result.supplier;
        this.isPriceLoading = false;
        this.hasDropoffWindowChanged =
          this.customerPriceResult.earliestDropoffDate != this.context.currentEarliestDropoffDate ||
          this.customerPriceResult.latestDropoffDate != this.context.currentLatestDropoffDate;
      })
      .catch((error) => {
        this.isPriceLoading = false;
        this.primeNgMessageService.add(Message.Error('Error', `Failed to calculate new price`));
      });
  }

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;
  protected readonly removeSlashesOnPaste = removeSlashesOnPaste;
  protected hasDropoffWindowChanged: boolean = false;
}
